/**
 * Creates an i18n default values object with empty strings for each language.
 * Main language is always first.
 *
 * @param {string} mainLanguage - The primary language code (e.g., "en", "sr")
 * @param {string[]} otherLanguages - Array of secondary language codes
 * @param {string} defaultValue - Default value for each language (default: "")
 * @returns {Object} Object with language codes as keys and defaultValue as values
 *
 * @example
 * makeI18nDefaultValue("sr", ["en", "de"])
 * // Returns: { sr: "", en: "", de: "" }
 */
export function makeI18nDefaultValue(
  mainLanguage = "en",
  otherLanguages = [],
  defaultValue = ""
) {
  const languages = [mainLanguage, ...(otherLanguages || [])].filter(Boolean);
  return languages.reduce((acc, lang) => {
    acc[lang] = defaultValue;
    return acc;
  }, {});
}

/**
 * Merges existing i18n data with default empty values for all event languages.
 * Useful for edit forms where you have partial data.
 *
 * @param {Object} existingData - Existing i18n data (e.g., { en: "Hello" })
 * @param {string} mainLanguage - The primary language code
 * @param {string[]} otherLanguages - Array of secondary language codes
 * @returns {Object} Merged object with all languages
 *
 * @example
 * mergeI18nWithDefaults({ en: "Hello" }, "sr", ["en", "de"])
 * // Returns: { sr: "", en: "Hello", de: "" }
 */
export function mergeI18nWithDefaults(
  existingData = {},
  mainLanguage = "en",
  otherLanguages = []
) {
  const defaults = makeI18nDefaultValue(mainLanguage, otherLanguages);
  return { ...defaults, ...existingData };
}

/**
 *
 * @param {Object} i18nObj - An object containing localized strings with language codes as keys (e.g., { en: "Hello", sr: "Zdravo" })
 * @param {String} defaultText - The default text to return if the localized version for the current language is not available
 * @param {Object} i18n - The i18n instance from react-i18next, used to determine the current language
 * @returns {String} The localized text based on the current language, or the default text if not available
 */
export function getI18nText(i18nObj, defaultText, i18n) {
  return i18nObj?.[i18n.language] || defaultText;
}
